import { Component, computed, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import {
  AgreementsClient,
  CreatedResponseDto,
  FormInstanceCreateDto,
  FormInstancesClient,
  FormScheduleMiniDto,
  FormSchedulesClient,
  UnityScheduleType,
} from '@unity/clients';
import { TextboxReadonlyControlComponent } from '@unity/components/forms/textbox-readonly-control/textbox-readonly-control.component';
import { SelectorDateControlComponent } from '@unity/forms/selector-date-control/selector-date-control.component';
import { SelectorDateRangeControlComponent } from '@unity/forms/selector-date-range-control/selector-date-range-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { dateFormControl, nonZeroFormControl, optionalDateFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';

class FormInstanceCreateSchema implements FormGroupControlSchema<FormInstanceCreateDto> {
  public agreement_id = nonZeroFormControl();
  public form_schedule_id = nonZeroFormControl();
  public open_date = dateFormControl();
  public due_date = optionalDateFormControl();
}

@Component({
  selector: 'unity-new-form-instance-page',
  imports: [
    KENDO_CARD,
    KENDO_LOADER,
    ReactiveFormsModule,
    TextboxReadonlyControlComponent,
    SelectorDateRangeControlComponent,
    SelectorDateControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    ActionSharedComponent,
  ],
  templateUrl: './new-form-instance-page.component.html',
  styleUrl: './new-form-instance-page.component.scss',
})
export class NewFormInstancePageComponent extends AbstractNewComponent<
  FormInstanceCreateDto,
  FormInstanceCreateSchema,
  CreatedResponseDto
> {
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly formInstancesClient = inject(FormInstancesClient);
  private readonly formSchedulesClient = inject(FormSchedulesClient);

  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $formScheduleId = input.required({ alias: 'formScheduleId', transform: (value: string) => parseInt(value) });

  protected override readonly $contentHeader = signal('Create New Form Instance');
  protected override createResourceFn$ = (form: FormGroup<FormInstanceCreateSchema>) => {
    const snapshot = form.getRawValue();
    return this.formInstancesClient.createOneForAgreement(snapshot.agreement_id, snapshot);
  };
  protected override redirectUrlFn = (_: CreatedResponseDto): UnityRouteUrlTrees => ['/agreement', this.$agreementId()];
  protected override newControlsFn() {
    return new FormInstanceCreateSchema();
  }

  protected agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsMini(agreementId),
  });

  protected formScheduleResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      const formScheduleId = this.$formScheduleId();
      return agreementId && formScheduleId ? { agreementId, formScheduleId } : undefined;
    },
    stream: ({ params: { agreementId, formScheduleId } }) => this.formSchedulesClient.getOneForAgreementAsMini(agreementId, formScheduleId),
  });

  protected readonly $loading = computed(() => this.agreementResource.isLoading() || this.formScheduleResource.isLoading());

  protected override onSubmit() {
    const formGroup = this.$formGroup();
    const agreement = this.agreementResource.value();
    const formSchedule = this.formScheduleResource.value();
    if (!agreement || !formSchedule) {
      throw new Error('Some of the required data to create this form instance is missing, please try again.');
    }

    formGroup.controls.agreement_id.patchValue(agreement.id);
    formGroup.controls.form_schedule_id.patchValue(formSchedule.id);
    super.onSubmit();
  }

  protected showDueDate(formSchedule: FormScheduleMiniDto | null) {
    return formSchedule?.type_enum_value == UnityScheduleType.Recurring;
  }

  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));
}
